import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';

export default function useCreditRightPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [statusCounts, setStatusCounts] = useState<Record<string, any>>({});
    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [resending, setResending] = useState(false);
    const [collapsedOps, setCollapsedOps] = useState<Set<string>>(new Set());
    const [receivableItem, setReceivableItem] = useState<any | null>(null);
    const [loadingReceivable, setLoadingReceivable] = useState(false);
    const pageSize = 100;

    const requestSeq = useRef(0);

    const fetchData = useCallback(async () => {
        const requestId = ++requestSeq.current;
        setIsLoading(true);
        try {
            const result = await collateralApi.getCreditRights({
                page, pageSize,
                status: statusFilter || undefined,
                search: search || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            if (requestId !== requestSeq.current) return;
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setStatusCounts(result.statusCounts || {});
            setSelectedIds(new Set());
        } catch (e: any) {
            if (requestId !== requestSeq.current) return;
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            if (requestId === requestSeq.current) {
                setIsLoading(false);
                setInitialLoading(false);
            }
        }
    }, [page, statusFilter, search, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);
    const handleRowClick = (row: any) => setSelectedItem(row);

    const toggleCollapse = (creditOperationId: string) => {
        setCollapsedOps(prev => {
            const next = new Set(prev);
            if (next.has(creditOperationId)) next.delete(creditOperationId);
            else next.add(creditOperationId);
            return next;
        });
    };

    const openReceivable = async (receivableId: string) => {
        if (!receivableId) return;
        setLoadingReceivable(true);
        try {
            const item = await collateralApi.getReceivable(receivableId);
            setReceivableItem(item);
        } catch (e: any) {
            toast.error(e?.data?.message || 'Falha ao carregar o recebível.');
        } finally {
            setLoadingReceivable(false);
        }
    };

    const closeReceivable = () => setReceivableItem(null);

    // Reload the currently open receivable (e.g. after resolving its city) so the
    // panel reflects the new IBGE status without closing.
    const reloadReceivable = async () => {
        if (receivableItem?.receivableId || receivableItem?.id) {
            await openReceivable(receivableItem.receivableId || receivableItem.id);
        }
    };

    const resendableIds = rows.filter(r => r.canResend).map(r => Number(r.id));

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = (checked: boolean) => {
        setSelectedIds(checked ? new Set(resendableIds) : new Set());
    };

    // Select/deselect every resendable row within a single operation group.
    const toggleSelectGroup = (groupIds: number[], checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            groupIds.forEach(id => { if (checked) next.add(id); else next.delete(id); });
            return next;
        });
    };

    const resendSelected = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        setResending(true);
        try {
            const res = await collateralApi.resendCreditRights(ids);
            toast.success(`Reenvio disparado (${res?.resent ?? 0} de ${res?.requested ?? ids.length}).`);
            refresh();
        } catch (e: any) {
            toast.error(e?.data?.message || 'Falha ao reenviar registros selecionados.');
        } finally {
            setResending(false);
        }
    };

    const resendOne = async (id: number) => {
        setResending(true);
        try {
            const res = await collateralApi.resendCreditRights([id]);
            toast.success(`Reenvio disparado (${res?.resent ?? 0} de ${res?.requested ?? 1}).`);
            refresh();
        } catch (e: any) {
            toast.error(e?.data?.message || 'Falha ao reenviar o registro.');
        } finally {
            setResending(false);
        }
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, statusCounts,
        statusFilter, setStatusFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        selectedItem, setSelectedItem,
        selectedIds, toggleSelect, toggleSelectAll, toggleSelectGroup, resendableIds,
        resending, resendSelected, resendOne,
        collapsedOps, toggleCollapse,
        receivableItem, loadingReceivable, openReceivable, closeReceivable, reloadReceivable,
        refresh, handleRowClick,
    };
}
